import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { TEST_IDS } from "@/constants/testIds";

const LOGO_URL = "https://customer-assets-agu9un31.emergentagent.net/job_ecf724c0-e7a0-4855-8934-70820d6dc685/artifacts/da2d482aee03c0e9_DV%20logo.png";

const navLinks = [
  { label: "Home", path: "/", id: TEST_IDS.nav.homeLink },
  { label: "About Us", path: "/about", id: "nav-link-about" },
  { label: "Our Services", path: "/services", id: "nav-link-services" },
  { label: "Our Clients", path: "/clients", id: "nav-link-clients" },
  { label: "Get Quote", path: "/quote", id: TEST_IDS.nav.quoteLink },
];

export default function Navbar() {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      data-testid={TEST_IDS.nav.container}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white ${
        scrolled
          ? "shadow-md shadow-black/8 border-b border-gray-200"
          : "border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <Link to="/" data-testid={TEST_IDS.nav.logo} className="flex items-center gap-3 shrink-0">
            <img src={LOGO_URL} alt="Devkuba Ventures" className="h-10 sm:h-12 w-auto" />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={link.id}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  location.pathname === link.path
                    ? "text-[#0EA5E9] bg-[#0EA5E9]/8"
                    : "text-[#1A1A1A] hover:text-[#0EA5E9] hover:bg-gray-100"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link to="/quote">
              <Button
                data-testid="nav-cta-quote"
                className="ml-3 bg-gradient-to-r from-[#00F2FE] via-[#0EA5E9] to-[#3B82F6] text-white font-semibold hover:opacity-90 transition-opacity text-sm px-5"
              >
                Start Project
              </Button>
            </Link>
          </div>

          {/* Mobile Nav */}
          <div className="md:hidden">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid={TEST_IDS.nav.mobileMenuBtn} className="text-[#1A1A1A]">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-white border-gray-200 w-72">
                <SheetHeader>
                  <SheetTitle className="text-left">
                    <img src={LOGO_URL} alt="Devkuba Ventures" className="h-10 w-auto" />
                  </SheetTitle>
                </SheetHeader>
                <div className="flex flex-col gap-2 mt-8">
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      data-testid={`mobile-${link.id}`}
                      onClick={() => setOpen(false)}
                      className={`px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                        location.pathname === link.path
                          ? "text-[#0EA5E9] bg-[#0EA5E9]/8"
                          : "text-[#1A1A1A] hover:text-[#0EA5E9] hover:bg-gray-100"
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                  <Link to="/quote" onClick={() => setOpen(false)} className="mt-4">
                    <Button className="w-full bg-gradient-to-r from-[#00F2FE] via-[#0EA5E9] to-[#3B82F6] text-white font-semibold">
                      Start Project
                    </Button>
                  </Link>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
}
